import { Link, useNavigate } from "@tanstack/react-router";
import { ClipboardCheck, FileText, UserPlus } from "lucide-react";
import { useState } from "react";
import { DetailSection } from "@/components/shared/detail-section";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { StatusPill } from "@/components/shared/status-badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useDebouncedValue } from "@/hooks/use-debounced-value";
import {
  type ReviewResultSummary,
  type ReviewTaskSummary,
  type TeamReviewResultSummary,
  useMyReviewResults,
  useMyReviewTasks,
  useTeamReviewResults,
} from "@/hooks/use-review-tasks";
import {
  type OpenReviewSubject,
  useMyReviewerRequests,
  useOpenReviewSubjects,
  useRequestReviewerAddition,
  useRequestReviewerRemoval,
} from "@/hooks/use-reviewer-requests";

/**
 * "Reviews to complete" — EPIC-060.
 *
 * Everyone's, including an admin's: being HR does not exempt you from your own
 * reviews, and having them on the same page is what stops them being forgotten.
 */

/** Date-only strings; `new Date("2026-07-31")` would shift them a day. */
function formatDate(value: string): string {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function MyReviewTasks() {
  const { data, isLoading, isError, error, refetch } = useMyReviewTasks();

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load your reviews"}
        onRetry={() => refetch()}
      />
    );
  }
  if (isLoading) return <Skeleton className="h-28 w-full" />;

  const tasks = data ?? [];
  if (tasks.length === 0) {
    return (
      <div className="rounded-lg border border-border">
        <EmptyState
          icon={ClipboardCheck}
          title="Nothing to fill in"
          description="Reviews you're asked to complete will appear here once a round opens."
        />
      </div>
    );
  }

  /*
    Grouped by round, and rounds with work left come first. One flat list
    interleaved two rounds and mixed handed-in with outstanding, so a manager
    with a team had to read every card to find the ones that still needed them.
  */
  const rounds = new Map<string, ReviewTaskSummary[]>();
  for (const task of tasks) {
    const forRound = rounds.get(task.cycleName);
    if (forRound) forRound.push(task);
    else rounds.set(task.cycleName, [task]);
  }
  const grouped = [...rounds.entries()]
    .map(([cycleName, rows]) => ({
      cycleName,
      outstanding: rows.filter((r) => r.statusKey !== "complete"),
      done: rows.filter((r) => r.statusKey === "complete"),
    }))
    .sort((a, b) => b.outstanding.length - a.outstanding.length);

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <RequestToReviewControl />
      </div>

      {grouped.map((group) => (
        <div key={group.cycleName} className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="font-medium">{group.cycleName}</h3>
            <span className="text-muted-foreground text-sm">
              {group.outstanding.length === 0
                ? "All handed in"
                : `${group.outstanding.length} still to do`}
            </span>
          </div>
          <ul className="divide-y divide-border rounded-lg border border-border">
            {/* Outstanding first within the round — the done ones are reference. */}
            {[...group.outstanding, ...group.done].map((task) => (
              <ReviewTaskRow key={task.reviewerId} task={task} />
            ))}
          </ul>
        </div>
      ))}

      <MyReviewerRequests />
    </div>
  );
}

/**
 * The caller's own requests, whatever their status — WI-206 AC-583. Their
 * own small section rather than folded into the task list: a request isn't
 * a task, and most people will have none at all.
 */
function MyReviewerRequests() {
  const { data } = useMyReviewerRequests();
  const requests = data ?? [];
  if (requests.length === 0) return null;

  return (
    <DetailSection title="Your requests">
      <ul className="divide-y divide-border rounded-lg border border-border">
        {requests.map((r) => (
          <li key={r.id} className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-3">
            <div className="min-w-0">
              <p className="font-medium text-sm">
                {r.requestType === "add"
                  ? `To review ${r.subjectName}`
                  : `To come off ${r.subjectName}'s review`}
              </p>
              <p className="truncate text-muted-foreground text-sm">{r.reason}</p>
              {r.decisionNote && (
                <p className="mt-1 text-muted-foreground text-xs">Note: {r.decisionNote}</p>
              )}
            </div>
            <StatusPill
              tone={
                r.statusKey === "approved"
                  ? "success"
                  : r.statusKey === "declined"
                    ? "danger"
                    : "neutral"
              }
              label={
                r.statusKey === "pending"
                  ? "Pending"
                  : r.statusKey === "approved"
                    ? "Approved"
                    : "Declined"
              }
            />
          </li>
        ))}
      </ul>
    </DetailSection>
  );
}

/** Ask onto a review for someone you worked with — WI-207. */
function RequestToReviewControl() {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<OpenReviewSubject | null>(null);
  const [reason, setReason] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data: subjects } = useOpenReviewSubjects(debouncedSearch);
  const request = useRequestReviewerAddition();

  const reset = () => {
    setSearch("");
    setSelected(null);
    setReason("");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <UserPlus className="size-4" aria-hidden />
          Request to review someone
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request to review someone</DialogTitle>
          <DialogDescription>
            For someone you actually worked with. HR decides, and it'll show up on your task list if
            approved.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {selected ? (
            <div className="flex items-center justify-between rounded-md border border-border p-2">
              <div>
                <p className="font-medium text-sm">{selected.subjectName}</p>
                <p className="text-muted-foreground text-xs">{selected.roleName}</p>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                Change
              </Button>
            </div>
          ) : (
            <div>
              <Label className="mb-1.5 block">Who did you work with?</Label>
              <Input
                placeholder="Search by name…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search.trim().length > 0 && (
                <ul className="mt-2 max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border">
                  {(subjects ?? []).length === 0 ? (
                    <li className="p-2 text-muted-foreground text-sm">No one matches.</li>
                  ) : (
                    (subjects ?? []).map((s) => (
                      <li key={s.reviewId}>
                        <button
                          type="button"
                          className="flex w-full flex-col items-start p-2 text-left hover:bg-muted/40"
                          onClick={() => setSelected(s)}
                        >
                          <span className="font-medium text-sm">{s.subjectName}</span>
                          <span className="text-muted-foreground text-xs">{s.roleName}</span>
                        </button>
                      </li>
                    ))
                  )}
                </ul>
              )}
            </div>
          )}
          <div>
            <Label className="mb-1.5 block" htmlFor="request-add-reason">
              Why?
            </Label>
            <Textarea
              id="request-add-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="How did you work together?"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            disabled={!selected || reason.trim().length === 0 || request.isPending}
            onClick={() => {
              if (!selected) return;
              request.mutate(
                { reviewId: selected.reviewId, reason: reason.trim() },
                { onSuccess: () => setOpen(false) },
              );
            }}
          >
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Your own finished reviews, from rounds that have closed.
 *
 * The task list is scoped to open rounds, so a completed review used to vanish
 * from this page the moment the round closed — which is exactly the moment you
 * become allowed to read it. This is the other half of the page.
 */
export function MyReviewResults() {
  const { data, isLoading, isError, error, refetch } = useMyReviewResults();

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load your results"}
        onRetry={() => refetch()}
      />
    );
  }
  if (isLoading) return <Skeleton className="h-20 w-full" />;

  const results = data ?? [];
  // Heading included, so the whole section disappears when there is nothing —
  // an empty "Your results" reads as a promise the page is failing to keep.
  if (results.length === 0) return null;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-semibold text-lg">Your results</h2>
        <p className="text-muted-foreground text-sm">
          Finished rounds, with everything that was said about you.
        </p>
      </div>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {results.map((result) => (
          <ReviewResultRow key={result.reviewId} result={result} />
        ))}
      </ul>
    </div>
  );
}

/**
 * Your team's reviews from closed rounds — the manager's way back to the
 * report to sign it off. Without it, a closed round left the manager's page
 * reading "Nothing to fill in" while the employee had already signed.
 *
 * Waiting-on-you first; the rest are reference.
 */
export function TeamReviewResults() {
  const { data, isLoading, isError, error, refetch } = useTeamReviewResults();

  if (isError) {
    return (
      <ErrorState
        message={error instanceof Error ? error.message : "Couldn't load your team's reviews"}
        onRetry={() => refetch()}
      />
    );
  }
  if (isLoading) return <Skeleton className="h-20 w-full" />;

  const results = data ?? [];
  if (results.length === 0) return null;

  const waiting = results.filter((r) => !r.managerSignedAt);
  const signed = results.filter((r) => r.managerSignedAt);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="font-semibold text-lg">Your team's reviews</h2>
          <p className="text-muted-foreground text-sm">
            Finished rounds for the people you manage. Open one to talk it through and sign off.
          </p>
        </div>
        {waiting.length > 0 && (
          <span className="text-muted-foreground text-sm">
            {waiting.length} waiting on your sign-off
          </span>
        )}
      </div>
      <ul className="divide-y divide-border rounded-lg border border-border">
        {[...waiting, ...signed].map((result) => (
          <TeamReviewResultRow key={result.reviewId} result={result} />
        ))}
      </ul>
    </div>
  );
}

function TeamReviewResultRow({ result }: { result: TeamReviewResultSummary }) {
  const needsYou = !result.managerSignedAt;
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 p-3 transition-colors hover:bg-muted/40 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
      <div className="min-w-0">
        <p className="font-medium">{result.subjectName}</p>
        <p className="truncate text-muted-foreground text-sm">
          {result.cycleName} · {result.roleName}
          {result.roleLevelName ? ` · ${result.roleLevelName}` : ""}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2 justify-self-start sm:justify-self-end">
        {needsYou ? (
          <StatusPill tone="warning" label="Needs your sign-off" />
        ) : (
          <StatusPill tone="success" label="You signed off" />
        )}
        <StatusPill
          tone={result.employeeSignedAt ? "success" : "neutral"}
          label={result.employeeSignedAt ? "Employee signed" : "Employee not signed yet"}
        />
      </div>
      <div className="col-span-2 flex justify-start sm:col-span-1 sm:justify-end">
        <Link
          to="/dashboard/reviews/reports/$reviewId"
          params={{ reviewId: result.reviewId }}
          className={
            needsYou
              ? "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 py-1.5 font-medium text-primary-foreground text-sm hover:bg-primary/90"
              : "inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 py-1.5 font-medium text-sm hover:bg-muted"
          }
        >
          <FileText className="size-4" aria-hidden />
          {needsYou ? "Review and sign off" : "Full review"}
        </Link>
      </div>
    </li>
  );
}

function ReviewResultRow({ result }: { result: ReviewResultSummary }) {
  return (
    <li className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-4 p-3 transition-colors hover:bg-muted/40">
      <div className="min-w-0">
        <p className="font-medium">{result.cycleName}</p>
        <p className="truncate text-muted-foreground text-sm">
          {result.roleName}
          {result.roleLevelName ? ` · ${result.roleLevelName}` : ""} · covering{" "}
          {formatDate(result.periodStart)} – {formatDate(result.periodEnd)}
        </p>
      </div>
      <Link
        to="/dashboard/reviews/reports/$reviewId"
        params={{ reviewId: result.reviewId }}
        className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 py-1.5 font-medium text-sm hover:bg-muted"
      >
        <FileText className="size-4" aria-hidden />
        Read it
      </Link>
    </li>
  );
}

function ReviewTaskRow({ task }: { task: ReviewTaskSummary }) {
  const navigate = useNavigate();
  const done = task.statusKey === "complete";

  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-2 p-3 transition-colors hover:bg-muted/40 sm:grid-cols-[minmax(0,1fr)_auto_auto]">
      <div className="min-w-0">
        <p className="font-medium">
          {/*
            A self-review is about you; saying "Yourself" is clearer than
            repeating your own name back at you.
          */}
          {task.relationshipKey === "self" ? "Your own review" : task.subjectName}
        </p>
        <p className="truncate text-muted-foreground text-sm">
          {task.relationshipName} · {task.cycleName} · due {formatDate(task.dueOn)}
        </p>
      </div>

      <div className="flex items-center gap-2 justify-self-start sm:justify-self-end">
        {done ? (
          <StatusPill tone="success" label="Handed in" />
        ) : (
          <>
            {/* Overdue is derived on read, so it can never be a stale flag. */}
            {task.isOverdue && <StatusPill tone="danger" label="Overdue" />}
            <span className="text-muted-foreground text-sm tabular-nums">
              {task.answered} of {task.total} answered
            </span>
          </>
        )}
      </div>

      <div className="col-span-2 flex justify-start gap-2 sm:col-span-1 sm:justify-end">
        {/*
          The report is the screen a manager takes into the conversation, and
          it had no link from anywhere they would look — "View" opens what THEY
          wrote, which is a different thing. Only for the manager: a peer or a
          reportee is not entitled to read the whole review, and `canViewReview`
          refuses them server-side regardless.
        */}
        {done && task.relationshipKey === "manager" && (
          <Link
            to="/dashboard/reviews/reports/$reviewId"
            params={{ reviewId: task.reviewId }}
            className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-border px-3 py-1.5 font-medium text-sm hover:bg-muted"
          >
            <FileText className="size-4" aria-hidden />
            Full review
          </Link>
        )}
        <Button
          variant={done ? "outline" : "default"}
          size="sm"
          onClick={() =>
            navigate({
              to: "/dashboard/reviews/tasks/$reviewerId",
              params: { reviewerId: task.reviewerId },
            })
          }
        >
          {done ? "View" : task.answered > 0 ? "Continue" : "Start"}
        </Button>
        {/*
          Not offered once handed in — AC-580 says "assigned", and asking off
          something already submitted has nothing left to change. Also not
          for a self-review: there's nobody else to ask instead.
        */}
        {!done && task.relationshipKey !== "self" && (
          <RequestRemovalControl reviewId={task.reviewId} subjectName={task.subjectName} />
        )}
      </div>
    </li>
  );
}

/** Ask off a review you're assigned to — WI-206. */
function RequestRemovalControl({
  reviewId,
  subjectName,
}: {
  reviewId: string;
  subjectName: string;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const request = useRequestReviewerRemoval();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setReason("");
      }}
    >
      <DialogTrigger asChild>
        <Button variant="ghost" size="sm" className="text-muted-foreground">
          Ask to come off this
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ask to come off {subjectName}'s review?</DialogTitle>
          <DialogDescription>
            HR decides. The review stays yours and actionable until then.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="mb-1.5 block" htmlFor="request-remove-reason">
            Why?
          </Label>
          <Textarea
            id="request-remove-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Why shouldn't you review them?"
          />
        </div>
        <DialogFooter>
          <Button
            disabled={reason.trim().length === 0 || request.isPending}
            onClick={() =>
              request.mutate(
                { reviewId, reason: reason.trim() },
                { onSuccess: () => setOpen(false) },
              )
            }
          >
            Send request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
